import { esc } from './sbchart.ts';

export type NameEntry = readonly [name: string, kind: string, id: number];
export interface NamedEntity { name: string; kind: string; id?: number }
export interface NameIndex {
  readonly targets: ReadonlyMap<string, string | null>;
  readonly re: RegExp | null;
}

const CASE_PARTIES = ['offender', 'victim', 'real', 'plaintiff', 'defendant', 'firm', 'lawyer', 'other'] as const;
type CaseParties = Partial<Record<typeof CASE_PARTIES[number], NamedEntity | null>>;

const target = ({ kind, id }: NamedEntity): string | null => {
  if (kind === 'gov') return 'gov:0';
  return /^[a-z]+$/.test(kind) && id != null && Number.isSafeInteger(id) && id >= 0 ? `${kind}:${id}` : null;
};

const add = (map: Map<string, string | null>, entity: NamedEntity) => {
  if (!entity.name?.trim()) return;
  const go = target(entity);
  if (!go) return;
  // 同一实体可在多个角色里重复出现;同名的不同实体则不猜。
  if (!map.has(entity.name)) map.set(entity.name, go);
  else if (map.get(entity.name) !== go) map.set(entity.name, null);
};

const index = (targets: ReadonlyMap<string, string | null>): NameIndex => {
  // 歧义名字也参与最长匹配,不能退而链接它里面更短的人名。
  const keys = [...targets.keys()].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return { targets, re: keys.length ? new RegExp(keys.join('|'), 'g') : null };
};

export const buildNameIndex = (entries: readonly NameEntry[]): NameIndex => {
  const map = new Map<string, string | null>();
  for (const [name, kind, id] of entries) add(map, { name, kind, id });
  return index(map);
};

/** 案卷中的真实当事人覆盖全城同名表;案内也重名时仍保留文字。 */
export const withCaseNames = (base: NameIndex, parties: CaseParties): NameIndex => {
  const context = new Map<string, string | null>();
  for (const role of CASE_PARTIES) {
    const entity = parties[role];
    if (entity) add(context, entity);
  }
  const map = new Map(base.targets);
  for (const [name, go] of context) map.set(name, go);
  return index(map);
};

/** 始终匹配原始正文,再逐段转义;名字里的HTML/正则字符也只是文字。 */
export const linkedText = (text: string, names: NameIndex): string => {
  if (!text || !names.re) return esc(text);
  let out = '', last = 0;
  for (const m of text.matchAll(names.re)) {
    const at = m.index ?? 0;
    const go = names.targets.get(m[0]);
    out += esc(text.slice(last, at)) + (go ? `<a data-go="${esc(go)}">${esc(m[0])}</a>` : esc(m[0]));
    last = at + m[0].length;
  }
  return out + esc(text.slice(last));
};
